import React, { useEffect, useState } from 'react';
import '../styles/MainContainer.css';
import './ProcessTemplates.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import Togglebox from '../input/Togglebox';
import OfflineBanner from '../shell/OfflineBanner';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import { useOffline } from '../context/OfflineContext';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import { invalidateAddOns } from '../../hooks/useAddOns';
import ProcessTemplateCard from '../process-template/ProcessTemplateCard';
import ProcessTemplateEditor from '../process-template/ProcessTemplateEditor';
import useTemplateReferenceData from '../process-template/useTemplateReferenceData';
import { apiErrorMessage } from '../process-template/templateModel';

// Server order: SortOrder, then name.
const compareTemplates = (a, b) => (Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0))
  || String(a.name ?? '').localeCompare(String(b.name ?? ''), 'de');

/**
 * Admin page /processtemplates (home tile "Prozessvorlagen"): template cards with rule badges, step
 * chain, flat rates and usage; actions Bearbeiten / Duplizieren / Archivieren|Wiederherstellen; "Neue Vorlage".
 * Loads GET /ProcessTemplate/GetAll?includeArchived=true once — the archived toggle only filters
 * locally, and every mutation patches the list from its response. The editor replaces the list in the
 * same card (components/process-template/ProcessTemplateEditor).
 */
function ProcessTemplates() {
  const { isOffline, onlineEpoch } = useOffline();
  const { byId } = useParticipantGroups();
  const { openPopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const refData = useTemplateReferenceData();

  const [templates, setTemplates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // { template: ProcessTemplateResponse | null (new), seq } — seq remounts the editor per opening.
  const [editing, setEditing] = useState(null);
  const [pendingArchive, setPendingArchive] = useState(null);
  const [actionError, setActionError] = useState({ title: '', text: '' });
  const [busy, setBusy] = useState(false);

  // onlineEpoch: retry after "Erneut verbinden" if the first load failed.
  useEffect(() => {
    if (loaded || isOffline) return undefined;
    let alive = true;
    setFailed(false);
    api.get('/ProcessTemplate/GetAll?includeArchived=true')
      .then(r => {
        if (!alive) return;
        setTemplates((Array.isArray(r.data) ? r.data : []).sort(compareTemplates));
        setLoaded(true);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [loaded, isOffline, onlineEpoch]);

  const upsert = (template) => {
    if (!template?.id) return;
    setTemplates(prev => {
      const exists = prev.some(t => t.id === template.id);
      const next = exists ? prev.map(t => (t.id === template.id ? template : t)) : [...prev, template];
      return next.sort(compareTemplates);
    });
  };

  const openEditor = (template) => {
    setEditing({ template, seq: Date.now() });
    window.scrollTo(0, 0);
  };

  const closeEditor = () => {
    setEditing(null);
    window.scrollTo(0, 0);
  };

  const handleSaved = (template) => {
    upsert(template);
    closeEditor();
  };

  const showActionError = (title, text) => {
    setActionError({ title, text });
    openPopup('templateactionerror', title);
  };

  const handleDuplicate = (template) => {
    if (busy) return;
    setBusy(true);
    startSuccessBarLoading('Vorlage wird dupliziert');
    api.post(`/ProcessTemplate/Duplicate/${template.id}`)
      .then(r => {
        // The copy references the same add-ons — the catalog's usage counts are refetched on its next load.
        invalidateAddOns();
        upsert(r.data);
        startSuccessBarSuccess(`Kopie „${r.data?.name ?? template.name}“ angelegt`);
      })
      .catch(err => {
        closeSuccessBar();
        showActionError('Duplizieren fehlgeschlagen', apiErrorMessage(err, 'Die Vorlage konnte nicht dupliziert werden.'));
      })
      .finally(() => setBusy(false));
  };

  const setArchived = (template, archived) => {
    if (busy) return;
    setBusy(true);
    startSuccessBarLoading(archived ? 'Vorlage wird archiviert' : 'Vorlage wird wiederhergestellt');
    api.put(`/ProcessTemplate/Archive/${template.id}?archived=${archived}`)
      .then(r => {
        upsert(r.data);
        startSuccessBarSuccess(archived ? 'Vorlage archiviert' : 'Vorlage wiederhergestellt');
      })
      .catch(err => {
        closeSuccessBar();
        showActionError(
          archived ? 'Archivieren fehlgeschlagen' : 'Wiederherstellen fehlgeschlagen',
          apiErrorMessage(err, 'Die Aktion ist fehlgeschlagen.'),
        );
      })
      .finally(() => setBusy(false));
  };

  const askArchive = (template) => {
    setPendingArchive(template);
    openPopup('confirmarchivetemplate', template.id);
  };

  const archivedCount = templates.filter(t => t.isArchived).length;
  const activeCount = templates.length - archivedCount;
  const visible = templates.filter(t => showArchived || !t.isArchived);

  const subtitle = !loaded
    ? (failed ? '' : 'Lädt …')
    : `${activeCount} ${activeCount === 1 ? 'Vorlage' : 'Vorlagen'}${archivedCount > 0 ? ` · ${archivedCount} archiviert` : ''}`;

  const actionsDisabled = isOffline || busy;
  const disabledReason = isOffline ? 'Offline nicht verfügbar' : undefined;

  let content;
  if (!loaded) {
    if (isOffline) content = <p className="processTemplatesMessage">Offline nicht verfügbar.</p>;
    else if (failed) content = <p className="processTemplatesMessage">Prozessvorlagen konnten nicht geladen werden.</p>;
    else content = null;
  } else if (visible.length === 0) {
    content = (
      <p className="processTemplatesMessage">
        {archivedCount > 0 && !showArchived
          ? 'Alle Vorlagen sind archiviert – über „Archivierte anzeigen“ wiederherstellen.'
          : 'Noch keine Vorlagen. Lege die erste über „Neue Vorlage“ an.'}
      </p>
    );
  } else {
    content = (
      <ul className="processTemplatesGrid">
        {visible.map(t => (
          <ProcessTemplateCard
            key={t.id}
            template={t}
            groupsById={byId}
            onEdit={openEditor}
            onDuplicate={handleDuplicate}
            onArchive={askArchive}
            onRestore={tpl => setArchived(tpl, false)}
            disabled={actionsDisabled}
            disabledReason={disabledReason}
          />
        ))}
      </ul>
    );
  }

  return (
    <React.Fragment>
      <main className="defaultMain">
        <OfflineBanner />
        {editing ? (
          <ProcessTemplateEditor
            key={editing.seq}
            template={editing.template}
            refData={refData}
            onClose={closeEditor}
            onSaved={handleSaved}
          />
        ) : (
          <React.Fragment>
            <div className="processTemplatesHeader">
              <div className="processTemplatesHeaderText">
                <h2 className="processTemplatesTitle">Prozessvorlagen</h2>
                {subtitle && <span className="processTemplatesSubtitle">{subtitle}</span>}
              </div>
              <ActionButton
                icon="add"
                label="Neue Vorlage"
                type={3}
                onClick={() => openEditor(null)}
                disabled={isOffline || !loaded}
              />
            </div>
            <div className="processTemplatesToolbar">
              <p className="formFieldHint">
                Vorlagen legen fest, welche Regeln, Schritte, Preise, Pauschalen &amp; Abzüge und Mails neue Prozesse und Events bekommen.
              </p>
              {/* Not a <label>: Togglebox is a label itself (nested labels would toggle twice). */}
              {archivedCount > 0 && (
                <div className="processTemplatesArchivedToggle">
                  <span className="processTemplatesArchivedText" onClick={() => setShowArchived(v => !v)}>
                    Archivierte anzeigen ({archivedCount})
                  </span>
                  <Togglebox checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
                </div>
              )}
            </div>
            {content}
          </React.Fragment>
        )}
      </main>

      <Popup
        name="confirmarchivetemplate"
        headerText="Vorlage archivieren"
        confirmText="Archivieren"
        type="confirmaction"
        onConfirm={() => { if (pendingArchive) setArchived(pendingArchive, true); }}
        withCancel
      >
        <p>
          <b>{pendingArchive?.name}</b> steht danach für neue Prozesse und Events nicht mehr zur Wahl. Laufende
          Prozesse und bestehende Events bleiben unverändert; du kannst die Vorlage jederzeit wiederherstellen.
        </p>
      </Popup>
      <Popup name="templateactionerror" headerText={actionError.title} confirmText="Ok" type="error">
        <p>{actionError.text}</p>
      </Popup>
    </React.Fragment>
  );
}

export default ProcessTemplates;
